"use client";

import {
  Button,
  Checkbox,
  FormInput,
  FormRoot,
  Label,
  Loader,
  Modal,
  RHF,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import type { SupplierListRow } from "@/modules/merchant/presentation/api/client";
import {
  useCreateSupplierMutation,
  useUpdateSupplierMutation,
} from "../api/queries";

const CreateSupplierFormSchema = z.object({
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ຜູ້ສະໜອງ").max(255),
  phone: z.string().max(64).optional(),
  refCode: z.string().max(128).optional(),
  note: z.string().max(2000).optional(),
});

const UpdateSupplierFormSchema = CreateSupplierFormSchema.extend({
  isActive: z.boolean(),
});

type CreateValues = z.infer<typeof CreateSupplierFormSchema>;
type UpdateValues = z.infer<typeof UpdateSupplierFormSchema>;

export type ShopSupplierFormModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shopId: string;
  mode: "create" | "edit";
  supplier?: SupplierListRow | null;
  onSuccess?: () => void;
};

export function ShopSupplierFormModal({
  open,
  onOpenChange,
  shopId,
  mode,
  supplier,
  onSuccess,
}: ShopSupplierFormModalProps) {
  const createMut = useCreateSupplierMutation(shopId);
  const updateMut = useUpdateSupplierMutation(shopId);

  const createForm = RHF.useForm<CreateValues>({
    resolver: zodResolver(CreateSupplierFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      refCode: "",
      note: "",
    },
  });

  const updateForm = RHF.useForm<UpdateValues>({
    resolver: zodResolver(UpdateSupplierFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      refCode: "",
      note: "",
      isActive: true,
    },
  });

  useEffect(() => {
    if (!open || !shopId) return;
    if (mode === "create") {
      createForm.reset({
        name: "",
        phone: "",
        refCode: "",
        note: "",
      });
    } else if (mode === "edit" && supplier) {
      updateForm.reset({
        name: supplier.name,
        phone: supplier.phone ?? "",
        refCode: supplier.refCode ?? "",
        note: supplier.note ?? "",
        isActive: supplier.isActive,
      });
    }
  }, [open, shopId, mode, supplier?.id, createForm, updateForm, supplier]);

  const isEdit = mode === "edit";
  const submitting = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? "ແກ້ໄຂຜູ້ສະໜອງ" : "ເພີ່ມຜູ້ສະໜອງ"}
      description={
        isEdit
          ? "ປ່ຽນຊື່ ຕິດຕໍ່ ຫຼືສະຖານະການໃຊ້ງານ"
          : "ບັນທຶກຜູ້ສະໜອງເພື່ອໃຊ້ກັບ PO ແລະກອງລາຍງານ"
      }
      size="md"
    >
      {isEdit ? (
        <FormRoot<UpdateValues>
          methods={updateForm}
          onSubmit={async (vals) => {
            if (!supplier) return;
            try {
              await updateMut.mutateAsync({
                supplierId: supplier.id,
                body: {
                  name: vals.name.trim(),
                  phone: vals.phone?.trim() || null,
                  refCode: vals.refCode?.trim() || null,
                  note: vals.note?.trim() || null,
                  isActive: vals.isActive,
                },
              });
              toast.success("ບັນທຶກຜູ້ສະໜອງແລ້ວ");
              onOpenChange(false);
              onSuccess?.();
            } catch (e) {
              toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກໄດ້");
            }
          }}
          className="space-y-4"
        >
          <FormInput name="name" label="ຊື່" requiredMark placeholder="ຊື່ຜູ້ສະໜອງ" />
          <FormInput name="phone" label="ໂທ" placeholder="020…" />
          <FormInput
            name="refCode"
            label="ລະຫັດອ້າງອີງ"
            placeholder="ລະຫັດພາຍໃນ / ຜູ້ສະໜອງ"
          />
          <FormInput
            name="note"
            label="ໝາຍເຫດ"
            placeholder="ບັນທຶກສັ້ນ (ທາງເລືອກ)"
          />
          <div className="flex items-center gap-2">
            <RHF.Controller
              name="isActive"
              control={updateForm.control}
              render={({ field }) => (
                <>
                  <Checkbox
                    id="supplier-isActive"
                    checked={field.value}
                    onCheckedChange={(c) => field.onChange(c === true)}
                  />
                  <Label
                    htmlFor="supplier-isActive"
                    className="cursor-pointer text-sm"
                  >
                    ເປີດໃຊ້ງານ
                  </Label>
                </>
              )}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ບັນທຶກ</span>
            </Button>
          </div>
        </FormRoot>
      ) : (
        <FormRoot<CreateValues>
          methods={createForm}
          onSubmit={async (vals) => {
            try {
              await createMut.mutateAsync({
                name: vals.name.trim(),
                phone: vals.phone?.trim() || null,
                refCode: vals.refCode?.trim() || null,
                note: vals.note?.trim() || null,
              });
              toast.success("ບັນທຶກຜູ້ສະໜອງແລ້ວ");
              onOpenChange(false);
              onSuccess?.();
            } catch (e) {
              toast.error(e instanceof Error ? e.message : "ບໍ່ສາມາດບັນທຶກໄດ້");
            }
          }}
          className="space-y-4"
        >
          <FormInput name="name" label="ຊື່" requiredMark placeholder="ຊື່ຜູ້ສະໜອງ" />
          <FormInput name="phone" label="ໂທ" placeholder="020…" />
          <FormInput
            name="refCode"
            label="ລະຫັດອ້າງອີງ"
            placeholder="ລະຫັດພາຍໃນ / ຜູ້ສະໜອງ"
          />
          <FormInput
            name="note"
            label="ໝາຍເຫດ"
            placeholder="ບັນທຶກສັ້ນ (ທາງເລືອກ)"
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader /> : null}
              <span className="ml-2">ເພີ່ມຜູ້ສະໜອງ</span>
            </Button>
          </div>
        </FormRoot>
      )}
    </Modal>
  );
}
